import Button, { type ButtonProps } from '@builder4/shared/components/Button';
import { ROOT_NODE, type Node, type NodeHelpersType } from '@craftjs/core';
import { InspectorButton } from '@/components/inspector/InspectorButton/InspectorButton';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { useAppSelector } from '@/store/hooks';
import { selectEffectiveBreakpoint, selectIsFullscreenPreview } from '@/store/system/selectors';
import type { Breakpoint } from '@builder4/shared/types/layout';

// In fullscreen, CSS @container queries cannot be relied upon because the nearest
// container ancestor may not match expected thresholds. Instead, merge the effective
// breakpoint's overrides into the base props and strip mobile/tablet sub-objects so
// the button renders the correct breakpoint's styles via JS, matching Container/Resizer.
export function resolveFullscreenButtonProps(
  props: Partial<ButtonProps>,
  breakpoint: Breakpoint,
): Partial<ButtonProps> {
  const { mobile, tablet, ...baseProps } = props;
  const bpOverrides =
    breakpoint === 'mobile' ? mobile : breakpoint === 'tablet' ? tablet : undefined;
  return bpOverrides ? { ...baseProps, ...bpOverrides } : baseProps;
}

const EditorButton = (props: Partial<ButtonProps>) => {
  const isFullscreenPreview = useAppSelector(selectIsFullscreenPreview);
  const effectiveBreakpoint = useAppSelector(selectEffectiveBreakpoint);

  if (isFullscreenPreview) {
    return <Button {...resolveFullscreenButtonProps(props, effectiveBreakpoint)} />;
  }

  return <Button {...props} />;
};

EditorButton.craft = {
  ...Button.craft,
  related: {
    toolbar: InspectorButton,
  },
  rules: {
    canDrag: (node: Node, helpers: NodeHelpersType) => {
      if (node.data.custom.isLocked) return false;
      const breakpoint = helpers(ROOT_NODE).get().data.props.breakpoint || 'desktop';
      const position = getBreakpointProp(
        node.data.props as Partial<ContainerProps>,
        'position',
        breakpoint,
      );

      return position === 'relative' || position === 'sticky';
    },
  },
};

export default EditorButton;
